<template>
  <div class="list-apply-room-discount-record-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('listApplyRoomDiscountRecord.title')"
      :subtitle="listApplyRoomDiscountRecordsInfo.conditions.roomName || $t('listApplyRoomDiscountRecord.page.subtitle')">
      <template #extra>
        <el-button icon="el-icon-back" @click="_goBack">
          {{ $t('common.back') }}
        </el-button>
        <el-button type="primary" icon="el-icon-plus" @click="_openAddModal(listApplyRoomDiscountRecordsInfo.applyRoomDiscount)">
          {{ $t('common.add') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="listApplyRoomDiscountRecordsInfo.total" :density="density" :show-column-setting="false"
        :show-refresh="true" @update:density="density = $event"
        @refresh="_listApplyRoomDiscountRecords(page.current, page.size)">
        <template #left>
          <span class="ds-panel__title">{{ $t('listApplyRoomDiscountRecord.title') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="listApplyRoomDiscountRecordsInfo.listApplyRoomDiscountRecords" :loading="loading"
        :error="hasError" :error-message="errorMessage" :density="density" border
        :empty-description="$t('listApplyRoomDiscountRecord.page.empty')"
        @retry="_listApplyRoomDiscountRecords(page.current, page.size)">
        <el-table-column prop="ardrId" :label="$t('listApplyRoomDiscountRecord.id')" align="center" width="140">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.ardrId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="roomName" :label="$t('listApplyRoomDiscountRecord.room')" align="left" min-width="130"
          show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.roomName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="createUserName" :label="$t('listApplyRoomDiscountRecord.operator')" align="center"
          width="120">
          <template slot-scope="scope">
            {{ scope.row.createUserName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="createTime" :label="$t('listApplyRoomDiscountRecord.createTime')" align="center" width="180">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatDateTimeSec(scope.row.createTime) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="stateName" :label="$t('listApplyRoomDiscountRecord.status')" align="center" width="120">
          <template slot-scope="scope">
            <ds-status-tag :type="resolveState(scope.row)" :label="scope.row.stateName || '-'" />
          </template>
        </el-table-column>
        <el-table-column prop="isTrueName" :label="$t('listApplyRoomDiscountRecord.isViolation')" align="center"
          width="120">
          <template slot-scope="scope">
            {{ scope.row.isTrueName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="remark" :label="$t('listApplyRoomDiscountRecord.remark')" align="left" min-width="160"
          show-overflow-tooltip>
          <template slot-scope="scope">
            <span class="ds-text-muted">{{ scope.row.remark || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.operation')" align="center" width="170" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="text" @click="_openApplyRoomDiscountRecordDetailsModel(scope.row)">
              {{ $t('common.viewDetail') }}
            </el-button>
            <el-button size="mini" type="text" class="ds-btn-danger"
              @click="_openDeleteApplyRoomDiscountRecordModel(scope.row)">
              {{ $t('common.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="listApplyRoomDiscountRecordsInfo.total" :page="page.current" :size="page.size"
        :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange">
        <template #summary>
          <span class="ds-tip-text">{{ $t('listApplyRoomDiscountRecord.page.tip') }}</span>
        </template>
      </ds-pagination-bar>
    </div>

    <apply-room-discount-record ref="applyRoomDiscountRecord" @success="handleSuccess" />
    <delete-apply-room-discount-record ref="deleteApplyRoomDiscountRecord" @success="handleSuccess" />
  </div>
</template>

<script>
import { queryApplyRoomDiscountRecord } from '@/api/fee/listApplyRoomDiscountRecordApi'
import ApplyRoomDiscountRecord from '@/components/fee/applyRoomDiscountRecord'
import DeleteApplyRoomDiscountRecord from '@/components/fee/deleteApplyRoomDiscountRecord'
import { getCommunityId } from '@/api/community/communityApi'
import { formatDateTimeSec } from '@/utils/format'
import { resolveStatus } from '@/utils/statusMap'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'ListApplyRoomDiscountRecord',
  components: {
    ApplyRoomDiscountRecord,
    DeleteApplyRoomDiscountRecord,
    DsPageHeader,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      listApplyRoomDiscountRecordsInfo: {
        listApplyRoomDiscountRecords: [],
        applyRoomDiscount: [],
        total: 0,
        records: 1,
        conditions: {
          ardId: '',
          roomId: '',
          roomName: '',
          stateName: '',
          state: '',
          communityId: ''
        }
      },
      page: {
        current: 1,
        size: 10
      }
    }
  },
  created() {
    this.listApplyRoomDiscountRecordsInfo.conditions.communityId = getCommunityId()
    this.listApplyRoomDiscountRecordsInfo.conditions.ardId = this.$route.query.ardId
    this.listApplyRoomDiscountRecordsInfo.conditions.state = this.$route.query.state
    this.listApplyRoomDiscountRecordsInfo.conditions.stateName = this.$route.query.stateName
    this.listApplyRoomDiscountRecordsInfo.conditions.roomId = this.$route.query.roomId
    this.listApplyRoomDiscountRecordsInfo.conditions.roomName = this.$route.query.roomName
    this._listApplyRoomDiscountRecords(this.page.current, this.page.size)
  },
  methods: {
    async _listApplyRoomDiscountRecords(page, size) {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          page: page,
          row: size,
          ...this.listApplyRoomDiscountRecordsInfo.conditions
        }
        const { data, total } = await queryApplyRoomDiscountRecord(params)
        this.listApplyRoomDiscountRecordsInfo.listApplyRoomDiscountRecords = Array.isArray(data) ? data : []
        this.listApplyRoomDiscountRecordsInfo.total = total || 0
      } catch (error) {
        console.error('获取房屋优惠申请记录失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('listApplyRoomDiscountRecord.page.loadFailed')
        this.$message.error(this.$t('listApplyRoomDiscountRecord.fetchError'))
      } finally {
        this.loading = false
      }
    },
    _openAddModal(applyRoomDiscount) {
      applyRoomDiscount.push(this.listApplyRoomDiscountRecordsInfo.conditions.ardId)
      applyRoomDiscount.push(this.listApplyRoomDiscountRecordsInfo.conditions.state)
      applyRoomDiscount.push(this.listApplyRoomDiscountRecordsInfo.conditions.stateName)
      applyRoomDiscount.push(this.listApplyRoomDiscountRecordsInfo.conditions.roomId)
      applyRoomDiscount.push(this.listApplyRoomDiscountRecordsInfo.conditions.roomName)
      this.$refs.applyRoomDiscountRecord.open(applyRoomDiscount)
    },
    _openDeleteApplyRoomDiscountRecordModel(applyRoomDiscountRecord) {
      this.$refs.deleteApplyRoomDiscountRecord.open(applyRoomDiscountRecord)
    },
    _openApplyRoomDiscountRecordDetailsModel(applyRoomDiscountRecord) {
      this.$router.push({
        path: '/views/fee/listApplyRoomDiscountRecordDetails',
        query: {
          ardrId: applyRoomDiscountRecord.ardrId,
          roomName: applyRoomDiscountRecord.roomName,
          state: applyRoomDiscountRecord.state
        }
      })
    },
    handleSuccess() {
      this._listApplyRoomDiscountRecords(this.page.current, this.page.size)
    },
    handleSizeChange(val) {
      this.page.size = val
      this.page.current = 1
      this._listApplyRoomDiscountRecords(this.page.current, val)
    },
    handleCurrentChange(val) {
      this.page.current = val
      this._listApplyRoomDiscountRecords(val, this.page.size)
    },
    _goBack() {
      this.$router.go(-1)
    },
    /**
     * 优惠申请状态：复用 applyRoomDiscount 域映射，未命中时安全降级为 muted
     */
    resolveState(row) {
      if (!row) return 'muted'
      const resolved = resolveStatus(row.state, {
        domain: 'applyRoomDiscount',
        text: row.stateName || '',
        fallbackLabel: row.stateName || ''
      })
      return resolved.type
    },
    formatDateTimeSec(value) {
      return formatDateTimeSec(value)
    }
  }
}
</script>

<style lang="scss" scoped>
.list-apply-room-discount-record-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}

/* 辅助文本 */
.ds-text-muted {
  color: var(--ds-gray-500);
}

/* 危险操作文字按钮 */
.ds-btn-danger {
  color: var(--ds-danger) !important;
}
</style>
